import React from 'react';
import { Link } from 'react-router-dom';
import ServiceHero from '../../components/services/ServiceHero';
import { Footer } from '../../components/Footer';
import { Check, CalendarCheck, TrendingDown } from 'lucide-react';

const MonthlyPlansPage = () => {

  const plans = [
    {
      tier: '🥉',
      name: 'Starter Plan',
      price: 8000000,
      sessions: '4 sessions of 2h (1 per week)',
      unitValue: 9600000,
      savings: 1600000,
      savingsPercent: 17,
      features: [
        '4 sessions of 2h (1 per week)',
        'Standard equipment: 2 cam + 2 mics',
        'Technician included every session',
        'Raw files delivered after each session',
        'Priority booking over hourly clients'
      ]
    },
    {
      tier: '🥈',
      name: 'Creator Plan',
      price: 18000000,
      sessions: '8 sessions of 2h (2 per week)',
      unitValue: 22400000,
      savings: 3200000,
      savingsPercent: 20,
      popular: true,
      features: [
        '8 sessions of 2h (2 per week)',
        'Standard equipment: 2 cam + 2 mics',
        'Technician included every session',
        '2 free edited reels per month',
        'Raw files delivered after each session',
        'Priority booking — slots blocked in advance',
        '-10% on all add-on services that month'
      ]
    },
    {
      tier: '🥇',
      name: 'Pro Plan',
      price: 32000000,
      sessions: '2 full days of 8h',
      unitValue: 44800000,
      savings: 9600000,
      savingsPercent: 29,
      features: [
        '2 full days of 8h',
        'Full equipment: 3 cam + 4 mics',
        'Technician included',
        '4 free edited episodes per month (basic edit)',
        '6 free edited reels per month',
        'Priority booking — 2 weeks advance reservation',
        '-15% on all add-on services that month'
      ]
    },
    {
      tier: '💎',
      name: 'Agency Plan',
      price: 55000000,
      sessions: '3 full days of 8h',
      unitValue: 76000000,
      savings: 20000000,
      savingsPercent: 28,
      features: [
        '3 full days of 8h',
        'Full premium equipment',
        'Dedicated senior technician',
        'Unlimited editing of all episodes that month',
        '10 free edited reels per month',
        '1 personal brand photo session included per month',
        'Dedicated account manager — single point of contact',
        'Exclusive weekly reserved time slots',
        '-20% on all additional services'
      ]
    }
  ];

  const comparisonData = [
    { pack: 'Starter', sessions: '4 x 2h', price: 'Rp 8,000,000', unitValue: 'Rp 9,600,000', savings: '-17%' },
    { pack: 'Creator', sessions: '8 x 2h', price: 'Rp 18,000,000', unitValue: 'Rp 22,400,000', savings: '-20%' },
    { pack: 'Pro', sessions: '2 full days (8h)', price: 'Rp 32,000,000', unitValue: 'Rp 44,800,000', savings: '-29%' },
    { pack: 'Agency', sessions: '3 full days (8h)', price: 'Rp 55,000,000', unitValue: 'Rp 76,000,000', savings: '-28%' }
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24">
      <ServiceHero
        tag="MONTHLY PLANS"
        title="Studio Subscriptions"
        description="Regular studio access at a discounted rate. Perfect for content creators who produce consistently."
        bookLink="/services/monthly-plans/book"
        bookLabel="Get Started"
        heroImage="https://images.unsplash.com/photo-1648522290910-38a6a3fd3684?w=1600&q=80"
        metaTitle="Monthly Plans | Canggu Podcast Studio"
        metaDescription="Monthly studio subscription plans in Canggu, Bali. Regular access at discounted rates for consistent creators."
      />

      {/* Comparison Table */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-8 text-center">
            <TrendingDown className="inline w-8 h-8 text-green-400 mr-3" />
            Compare Plans & Savings
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#121212]">
                  <th className="text-left p-4 text-white border border-[#262626]">Plan</th>
                  <th className="text-left p-4 text-white border border-[#262626]">Sessions/month</th>
                  <th className="text-left p-4 text-white border border-[#262626]">Price/month</th>
                  <th className="text-left p-4 text-white border border-[#262626]">Unit value</th>
                  <th className="text-left p-4 text-green-400 border border-[#262626]">Savings</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, i) => (
                  <tr key={i}>
                    <td className="p-4 border border-[#262626] text-white font-semibold">{row.pack}</td>
                    <td className="p-4 border border-[#262626] text-gray-400">{row.sessions}</td>
                    <td className="p-4 border border-[#262626] text-[#F5A623] font-semibold">{row.price}</td>
                    <td className="p-4 border border-[#262626] text-gray-500 line-through">{row.unitValue}</td>
                    <td className="p-4 border border-[#262626] text-green-400 font-bold">{row.savings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Plans Detail */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">Plan Details</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`p-6 border ${
                  plan.popular 
                    ? 'border-[#F5A623] bg-[#F5A623]/5' 
                    : 'border-[#262626] bg-[#121212]'
                } relative`}
              >
                {plan.popular && (
                  <span className="absolute -top-3 right-6 px-3 py-1 bg-[#F5A623] text-black text-xs font-semibold">
                    BEST VALUE
                  </span>
                )}
                
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">{plan.tier}</span>
                  <div>
                    <h3 className="text-2xl font-heading font-semibold text-white">{plan.name}</h3>
                    <p className="text-sm text-gray-400">{plan.sessions}</p>
                  </div>
                </div>
                
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-heading font-bold text-[#F5A623]">
                    Rp {(plan.price / 1000000).toFixed(0)}M
                  </span>
                  <span className="text-gray-500">/month</span>
                </div>
                
                <div className="flex items-center gap-2 mb-6">
                  <span className="text-sm text-gray-500 line-through">
                    Rp {(plan.unitValue / 1000000).toFixed(1)}M value
                  </span>
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-semibold">
                    Save Rp {(plan.savings / 1000000).toFixed(1)}M/month
                  </span>
                </div>
                
                <ul className="space-y-3">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                      <Check className="w-4 h-4 text-[#F5A623] mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
            Ready to Start Your Monthly Plan?
          </h2>
          <p className="text-gray-400 mb-8">
            Lock in your studio time and save up to 29% every month.
          </p>
          <Link
            to="/services/monthly-plans/book"
            className="inline-block px-12 py-4 bg-[#F5A623] text-black font-semibold text-lg hover:bg-[#FFB84D] transition-colors"
            data-testid="book-now-cta"
          >
            Request My Plan
          </Link>
        </div>
      </section>

      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default MonthlyPlansPage;
